import React, { memo } from 'react'
import { Link } from 'react-router-dom'
import { formatPrice } from '../lib/supabaseClient'

// ===== STATIC PRODUCT COVER IMAGE MAP =====
// Each product has ONE static cover image for catalog cards.
// The full image gallery is only shown on the product detail page (/product/:slug).
const CARD_COVER_IMAGE = {
  'ss-da15s':  '/products/SS-DA15S.png',
  'ss-da18s':  '/products/SS-DA18S.png',
  'ss-h8pro':  '/products/SS-H8PRO.png',
  'ss-j4012a': '/products/SJ4012.png',
  'ss-j4015a': '/products/SJ4015.png',
  'ss-r10a':   '/products/SS-10RA.png',
  'ss-r12a':   '/products/SS-12RA.png',
  'ss-r15a':   '/products/SS-15RA.png',
  'ss-280a':   '/products/ss-280a.png',
  'ss200m-uv': '/products/ss200m-uv.png',
  'ss-pa-21':   '/products/SS-PA_2.1.png',
  'ss-dd1238a': '/products/SS-DD1238A.png',
}

function getCardCoverImage(slug) {
  return CARD_COVER_IMAGE[slug] || `/products/${slug}.png`
}

const ProductCard = memo(function ProductCard({ product, index = 0, isDealer = false }) {
  const {
    slug,
    name,
    category,
    price,
    dealer_price,
  } = product

  // isDealer passed as prop from CatalogPage
  const displayPrice = price ?? 0
  const showDealerPrice = isDealer && dealer_price != null
  const coverImage = getCardCoverImage(slug)

  return (
    <Link
      to={`/product/${slug}`}
      className="group animate-fade-in-up opacity-0 block"
      style={{ animationDelay: `${index * 80}ms`, animationFillMode: 'forwards' }}
      aria-label={`${name} -- ${formatPrice(displayPrice)}`}
    >
      <div className="bg-white border border-gray-100 rounded-2xl overflow-hidden shadow-sm transition-shadow duration-300 group-hover:shadow-lg">
        {/* Static product image — NO carousel, NO pagination dots */}
        <div className="relative aspect-square overflow-hidden bg-transparent select-none">
          <div className="w-full h-full flex items-center justify-center p-6 group-hover:-translate-y-[10px] transition-transform duration-400">
            <img
              src={coverImage}
              alt={name}
              className="max-w-full max-h-full object-contain drop-shadow-[0_20px_30px_rgba(0,0,0,0.15)] group-hover:drop-shadow-[0_30px_40px_rgba(0,0,0,0.2)] transition-all duration-400"
              draggable={false}
              loading="lazy"
              onError={(e) => { e.target.src = `/products/${slug}.png` }}
            />
          </div>
          {/* "YOUR PRICE" badge for dealers */}
          {showDealerPrice && (
            <div className="absolute top-3 left-3">
              <span className="inline-flex items-center rounded-full bg-red-600 px-2.5 py-1 text-[10px] font-semibold text-white uppercase tracking-wider">
                ВАША ЦЕНА
              </span>
            </div>
          )}
        </div>
        <div className="p-5 pt-4">
          <p className="text-[11px] font-semibold uppercase tracking-[0.06em] text-gray-400 mb-1.5">
            {category}
          </p>
          <h3 className="text-[15px] font-semibold text-gray-900 leading-snug mb-3 overflow-hidden"
              style={{ display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical' }}>
            {name}
          </h3>
          {/* ===== PRICE BLOCK ===== */}
          {showDealerPrice ? (
            /* Dealer: strikethrough grey retail + large red dealer price */
            <div className="flex items-baseline gap-2 flex-wrap">
              <span className="text-sm text-gray-400 line-through decoration-gray-300">
                {formatPrice(displayPrice)}
              </span>
              <span className="text-[19px] font-bold text-red-600 tracking-tight">
                {formatPrice(dealer_price)}
              </span>
            </div>
          ) : (
            /* Guest: one standard black price */
            <p className="text-[19px] font-bold text-gray-900 tracking-tight">
              {formatPrice(displayPrice)}
            </p>
          )}
        </div>
      </div>
    </Link>
  )
})

export default ProductCard
